import { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteFallback } from './RouteFallback';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import TopNavbar from './TopNavbar';
import CommandPalette from './CommandPalette';
import { TestModeBanner } from './TestModeBanner';
import { SupplierBanner } from './SupplierBanner';
import { SupplierAccessRemoved } from './SupplierAccessRemoved';
import { useCompany } from '@/contexts/CompanyContext';
import ScrollToTop from './ScrollToTop';
import { FeedbackButton } from '@/components/feedback/FeedbackButton';
import { TrackrsAttentionIntroModal } from '@/components/integration/attention/TrackrsAttentionIntroModal';
import { WhatsNewLaunchDialog } from '@/components/whats-new/WhatsNewLaunchDialog';
import { idpEnabled } from '@/lib/idp';
import { shouldOpenCommandPalette } from './commandPaletteShortcuts';

export default function AdminLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const location = useLocation();
  const { supplierAccessRemoved } = useCompany();
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setMobileMenuOpen(false); };
    const desktop = window.matchMedia('(min-width: 1024px)');
    const resize = () => { if (desktop.matches) setMobileMenuOpen(false); };
    window.addEventListener('keydown', escape);
    desktop.addEventListener('change', resize);
    return () => { window.removeEventListener('keydown', escape); desktop.removeEventListener('change', resize); };
  }, [mobileMenuOpen]);
  useEffect(() => {
    const openPalette = (event: KeyboardEvent) => {
      if (!shouldOpenCommandPalette(event)) return;
      event.preventDefault();
      setCommandPaletteOpen(true);
    };

    window.addEventListener('keydown', openPalette);
    return () => window.removeEventListener('keydown', openPalette);
  }, []);

  return (
    <div className="min-h-screen bg-surface dark:bg-dark-surface">
      <ScrollToTop />
      {mobileMenuOpen && <button aria-label="Luk menu" className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />}
      <div className={mobileMenuOpen ? 'block' : 'hidden lg:block'}><Sidebar /></div>
      <div className="ml-0 lg:ml-[270px] min-h-screen min-w-0 flex flex-col" inert={mobileMenuOpen ? true : undefined}>
        <TopNavbar onOpenMobileMenu={() => setMobileMenuOpen(true)} />
        <TestModeBanner />
        <SupplierBanner />
        <main className="flex-1 p-4 sm:p-6">
          {supplierAccessRemoved ? <SupplierAccessRemoved /> : <ErrorBoundary><Suspense fallback={<RouteFallback />}><Outlet /></Suspense></ErrorBoundary>}
        </main>
      </div>
      <FeedbackButton />
      {/* Same boundary as the sidebar's IdpNavSections: the modal reads the .NET
          client, which only exists on the IDP path. */}
      {idpEnabled && <TrackrsAttentionIntroModal />}
      {idpEnabled && <WhatsNewLaunchDialog />}
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
    </div>
  );
}
